/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { endOfMonth, format, parseISO, startOfMonth } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import { isToday } from 'date-fns/esm';
import api from '../../../services/api';

import { formatPrice, TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useClinica } from '../../../hooks/clinica';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import Select from '../../../components/Select';
import SelectSync, { IOption } from '../../../components/SelectSync';
import Loading from '../../../components/Loading';

import getValidationErrors from '../../../utils/getValidationErrors';

import { arrayGroupBy } from '../../../utils/funcoes';

import { RelatorioHeader } from './styles';
import getResponseErrors from '../../../utils/getResponseErrors';
import { optionsTipoPagarOutrasClinicas } from '../../../utils/options';

interface IPagamento {
  descricao: string;
  valor: number;
  valorFormatado: string;
}

interface IRelatorio {
  CprCodigo: number;
  PacNome: string;
  CprValorPag: number;
  Perfil: string;
  CprDataPag: Date;
  CprDataPagFormatado: string;
  FormaValor: string;
  FormaPag: string;
  CprHistorico: string;
  CliNomeAbreviado: string;
  pagamento: IPagamento[];
}

interface IRelatorioFormatado {
  grupo: string;
  total: number;
  totalFormatado: string;
  dados: IRelatorio[];
}

interface ITotais {
  totalFormatado: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  clinicaSelecionada: string;
  tipo: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

const PagarOutraClinica: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica, imagensClinica } = useClinica();

  const [optionsOutrasClinicas, setOptionsOutrasClinicas] = useState<IOption[]>(
    [],
  );
  const [nomeClinica, setNomeClinica] = useState('');
  const [nomeTipo, setNomeTipo] = useState('');

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);

  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorioFormatado[]>();
  const [totais, setTotais] = useState<ITotais>();

  useEffect(() => {
    const outrasClinicas = optionsClinica.filter(
      opt => opt.value !== user.UsuClinica.toString(),
    );
    setOptionsOutrasClinicas(outrasClinicas);
  }, [optionsClinica, setOptionsOutrasClinicas, user.UsuClinica]);

  const logo =
    imagensClinica.find(img => img.nome === 'logoRelatorio')?.arquivo_url || '';

  const initialData = {
    dataInicio: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    dataFim: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    CliCodigo: '',
    tipo: '0',
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          clinicaSelecionada: Yup.string().required(),
          tipo: Yup.string().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        if (dados.clinicaSelecionada && dados.tipo) {
          const nomeCli =
            optionsOutrasClinicas.find(
              cli => cli.value === dados.clinicaSelecionada,
            )?.label || '';
          setNomeClinica(nomeCli);

          const nomeTip =
            optionsTipoPagarOutrasClinicas.find(
              tipo => tipo.value === dados.tipo,
            )?.label || '';
          setNomeTipo(nomeTip);
        }

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
        });

        const response = await api.get<IRelatorio[]>(
          `/relatorios/pagaroutrasclinicas?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&clinicaSelecionada=${dados.clinicaSelecionada}&tipo=${dados.tipo}`,
        );

        let totalGeral = 0;

        const dadosRetorno = response.data.filter(
          retorno => retorno.CprValorPag > 0,
        );

        const dadosFormatados = dadosRetorno.map(rel => {
          const CprDataPagFormatado = format(
            parseISO(rel.CprDataPag.toString()),
            'dd/MM/yyyy',
          );

          const doDia = isToday(dataInicioSelecionada);

          return {
            ...rel,

            CprDataPagFormatado,

            pagamento: rel.pagamento
              ? rel.pagamento.map(des => {
                  return { ...des, valorFormatado: formatPrice(des.valor) };
                })
              : [{ descricao: '', valor: 0, valorFormatado: 'R$ 0,00' }],
            doDia,
          };
        });

        const ordenado = arrayGroupBy(dadosFormatados, i => i.Perfil);

        const ordenadoFormatado = ordenado.map(ord => {
          const total = ord.reduce((tot, item) => {
            return tot + Number(item.CprValorPag);
          }, 0);
          totalGeral += total;

          return {
            grupo: ord[0].Perfil,
            total,
            totalFormatado: formatPrice(total),
            dados: ord,
          };
        });

        setDadosRelatorio(ordenadoFormatado);
        setTotais({
          totalFormatado: formatPrice(totalGeral),
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, optionsOutrasClinicas],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}

      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2">
            <h2 id="topoBusca">À Pagar Outras Unidades</h2>
          </div>

          <Form
            ref={formRefBusca}
            initialData={initialData}
            onSubmit={handleSubmitBusca}
          >
            <Row>
              <Col md={2}>
                <Input name="dataInicio" label="De" type="date" required />
              </Col>
              <Col md={2}>
                <Input name="dataFim" label="Até" type="date" required />
              </Col>
              <Col md={2}>
                <SelectSync
                  name="tipo"
                  label="Tipo"
                  options={optionsTipoPagarOutrasClinicas}
                  isClearable
                  handleSelectChange={handleOnChangeVazio}
                />
              </Col>
              <Col md={2}>
                <Select
                  name="clinicaSelecionada"
                  label="Clinica"
                  options={optionsOutrasClinicas}
                />
              </Col>
              <Col md={2}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        <div className="bg-white border p-2 my-3">
          <img src={logo} alt="Agenda facil" /> Impresso em{' '}
          {dataExibe?.dataHora}
        </div>

        <RelatorioHeader>
          <div className="d-flex flex-row-reverse justify-content-between">
            <span>&nbsp;</span>
            <span className="text-center font-weight-bold text-uppercase">
              Valores à Pagar
            </span>
            <span className="small">
              Período: 01/12/2021 à 31/12/2021 - Clinica: {nomeClinica} - Tipo:
              {nomeTipo}
            </span>
          </div>
        </RelatorioHeader>
        <div className="bg-white border p-2">
          {dadosRelatorio && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th style={{ width: '90px' }}>Data</th>
                    <th>Paciente</th>
                    <th style={{ width: '135px' }}>Reg.</th>
                    <th style={{ width: '140px' }}>Pagamento</th>
                    <th style={{ width: '140px' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <React.Fragment key={`des-${rel.grupo}`}>
                        <tr
                          style={{
                            fontSize: 'Small',
                            height: '30px',
                            fontWeight: 'bold',
                          }}
                        >
                          <td colSpan={2}>{rel.grupo}</td>
                          <td>Nº Registros: {rel.dados.length}</td>
                          <td>&nbsp;</td>
                          <td>Total: {rel.totalFormatado}</td>
                        </tr>
                        {rel.dados.map(relatorio => (
                          <tr
                            key={`despesas-${rel.grupo}-${relatorio.CprCodigo}`}
                            data-id={relatorio.CprCodigo}
                            style={{ fontSize: '.7rem', height: '30px' }}
                          >
                            <td style={{ width: '90px' }}>
                              {relatorio.CprDataPagFormatado}
                            </td>
                            <td colSpan={2}>{relatorio.PacNome}</td>

                            <td>
                              {relatorio.pagamento.map(pag => (
                                <span
                                  key={`pagD-${relatorio.CprCodigo}-${pag.descricao}`}
                                  className="d-block"
                                >
                                  {pag.descricao}
                                </span>
                              ))}
                            </td>
                            <td>
                              {relatorio.pagamento.map(pag => (
                                <span
                                  key={`pagV-${relatorio.CprCodigo}-${pag.descricao}`}
                                  className="d-block"
                                >
                                  {pag.valorFormatado}
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}

                  <tr
                    key="fdfdsfsdfdf"
                    style={{
                      fontSize: 'Small',
                      height: '30px',
                      fontWeight: 'bold',
                    }}
                  >
                    <td colSpan={6} style={{ textAlign: 'right' }}>
                      Total Geral: {totais?.totalFormatado}
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default PagarOutraClinica;
